import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { generalMessages } from 'src/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/features/moneyAccounts/utils/constants';
import { ServicePlanBillingPolicyFormSchemaValues } from 'src/features/servicePlanBillingPolicies/utils/formSchema';
import { BILLING_CYCLES, BILLING_TIME } from 'src/features/servicePlanBillingPolicies/utils/types';
import { BILLING_CYCLE_MAP, BILLING_TIME_MAP } from 'src/features/servicePlanBillingPolicies/utils/constants';
import { billingPoliciesMessages } from 'src/features/servicePlanBillingPolicies/utils/messages';
import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils';
import { PopoverPortal } from '@radix-ui/react-popover';
import { Calendar } from 'src/registry/ui/Calendar';
import dayjs from 'dayjs';

type ServicePlanBillingPolicyFormProps = {
    form: UseFormReturn<ServicePlanBillingPolicyFormSchemaValues>;
    onSubmit: (values: ServicePlanBillingPolicyFormSchemaValues) => void;
};

function ServicePlanBillingPolicyForm({ onSubmit, form }: ServicePlanBillingPolicyFormProps) {
    const { formatMessage, formatDate } = useIntl();

    const { valid_from, valid_to } = form.watch();
    const [openValidFromCalendar, setOpenValidFromCalendar] = React.useState<boolean>(false);
    const [openValidToCalendar, setOpenValidToCalendar] = React.useState<boolean>(false);

    const disableCalendarValidToDate = new Date(valid_from);
    const disableCalendarValidFromDate = valid_to ? new Date(valid_to) : new Date();
    disableCalendarValidToDate.setHours(0, 0, 0, 0);
    disableCalendarValidFromDate.setHours(0, 0, 0, 0);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => {
                    const selectedDate = field.value ? new Date(field.value) : undefined;

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(generalMessages.validFrom)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Popover open={openValidFromCalendar} onOpenChange={setOpenValidFromCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="date"
                                                variant="outline"
                                                className="w-full justify-start text-base font-normal"
                                                onClick={() => setOpenValidFromCalendar(true)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.CALENDAR}
                                                    className="text-primary-700 mr-1 size-4"
                                                />
                                                <span className={cn(!field.value && 'text-gray-500')}>
                                                    {field.value
                                                        ? formatDate(new Date(field.value))
                                                        : `${formatMessage(generalMessages.selectDate)}...`}
                                                </span>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverPortal>
                                            <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                                <Calendar
                                                    captionLayout="dropdown"
                                                    mode="single"
                                                    selected={selectedDate}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                            setOpenValidFromCalendar(false);
                                                        }
                                                    }}
                                                    disabled={
                                                        valid_to
                                                            ? {
                                                                  after: new Date(
                                                                      new Date(valid_to).setHours(0, 0, 0, 0),
                                                                  ),
                                                              }
                                                            : undefined
                                                    }
                                                />
                                            </PopoverContent>
                                        </PopoverPortal>
                                    </Popover>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => {
                    const selectedDate = field.value ? new Date(field.value) : undefined;

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{formatMessage(generalMessages.validTo)}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Popover open={openValidToCalendar} onOpenChange={setOpenValidToCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="date"
                                                variant="outline"
                                                className="w-full justify-start text-base font-normal"
                                                onClick={() => setOpenValidToCalendar(true)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.CALENDAR}
                                                    className="text-primary-700 mr-1 size-4"
                                                />
                                                <span className={cn(!field.value && 'text-gray-500')}>
                                                    {field.value
                                                        ? formatDate(new Date(field.value))
                                                        : `${formatMessage(generalMessages.selectDate)}...`}
                                                </span>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverPortal>
                                            <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                                <Calendar
                                                    captionLayout="dropdown"
                                                    mode="single"
                                                    selected={selectedDate}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                            setOpenValidToCalendar(false);
                                                        }
                                                    }}
                                                    disabled={{
                                                        before: new Date(
                                                            disableCalendarValidToDate.getTime() + 24 * 60 * 60 * 1000,
                                                        ),
                                                    }}
                                                />
                                            </PopoverContent>
                                        </PopoverPortal>
                                    </Popover>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <SelectFormField<ServicePlanBillingPolicyFormSchemaValues>
                form={form}
                name="billing_cycle"
                required
                label={formatMessage(billingPoliciesMessages.billingCycle)}
                options={Object.values(BILLING_CYCLES)}
                optionTranslationMap={BILLING_CYCLE_MAP}
            />
            <SelectFormField<ServicePlanBillingPolicyFormSchemaValues>
                form={form}
                name="billing_time"
                required
                label={formatMessage(billingPoliciesMessages.billingTime)}
                options={Object.values(BILLING_TIME)}
                optionTranslationMap={BILLING_TIME_MAP}
            />
            <SelectFormField<ServicePlanBillingPolicyFormSchemaValues>
                form={form}
                name="enable_proration"
                isBoolean={true}
                required={true}
                label={formatMessage(billingPoliciesMessages.enabledProrate)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
        </Form>
    );
}

export default ServicePlanBillingPolicyForm;
